<!--
  启动页（StartupView）

  目录
  1. 顶栏：品牌、主题切换、登录/头像与退出
  2. 主区：新建作品、我的作品 / 已发布作品 Tab
  3. 弹窗：新建作品、皮肤模板选择、皮肤预览
  4. 脚本：Store 与状态、顶栏溢出、云端列表、皮肤模板、工程打开/新建
  5. 样式：布局、列表、弹窗与竖屏适配
-->
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  NLayout,
  NLayoutHeader,
  NLayoutContent,
  NSpace,
  NButton,
  NCard,
  NList,
  NListItem,
  NThing,
  NEmpty,
  NModal,
  NForm,
  NFormItem,
  NInput,
  NAlert,
  NText,
  NPopconfirm,
  NTabs,
  NTabPane,
  useMessage,
} from 'naive-ui'
import AppSelectField, { type AppSelectOption } from '@/components/AppSelectField.vue'
import AuthAvatar from '@/components/AuthAvatar.vue'
import { useAuthStore } from '@/features/auth'
import { useWorkspaceStore } from '@/store/workspace'
import { useThemeStore } from '@/store/theme'
import type { CloudProjectListItem } from '@/features/project-cloud/list'
import { loadCloudProjectList } from '@/features/project-cloud/projectListLoader'
import BrowserUtil from '@/platform/oss/browserUtil'
import {
  getBuiltinUiTemplate,
  listBuiltinUiTemplates,
  type BuiltinUiTemplate,
} from '@/features/ui-template/builtinCatalog'
import PublishedProjectTab from '@/features/published-project/components/PublishedProjectTab.vue'
import { APP_DISPLAY_VERSION } from '@/config/appVersion'
import logoUrl from '@/assets/logo-start.png'
import {
  hasInvalidProjectNameChar,
  isProjectNameLengthValid,
  PROJECT_NAME_INVALID_CHAR_MESSAGE,
  PROJECT_NAME_LIMIT_MESSAGE,
  truncateProjectName,
} from '@/utils/projectNameLimit'

// ── 路由、Store 与页面状态 ──

const router = useRouter()
const message = useMessage()
const auth = useAuthStore()
const workspace = useWorkspaceStore()
const themeStore = useThemeStore()

const openError = ref('')

const newModalOpen = ref(false)
const newGameName = ref('')
const newResolution = ref('1280x720')
const newTemplateId = ref<string>('')
const templatePickerOpen = ref(false)
const templatePickerDraftId = ref<string>('')
const templatePreviewOpen = ref(false)
const templatePreviewId = ref<string>('')
const newSubmitLoading = ref(false)
const cloudProjects = ref<CloudProjectListItem[]>([])
const cloudListLoading = ref(false)
const cloudListError = ref('')
const cloudListConfigured = ref(true)
const startupTab = ref<'cloud' | 'published'>('cloud')
const publishedTabRef = ref<InstanceType<typeof PublishedProjectTab> | null>(null)
const publishedListLoading = ref(false)
const canUseProjectEntry = computed(() => auth.loggedIn && Boolean(auth.uid))
let latestCloudListToken = 0

// ── 顶栏单行溢出（先藏昵称，再藏主题按钮）──

const startupHeaderInnerRef = ref<HTMLElement | null>(null)
const hideStartupHeaderUserName = ref(false)
const hideStartupHeaderThemeBtn = ref(false)
let startupHeaderResizeObserver: ResizeObserver | null = null

/**
 * 顶栏保持单行：先隐藏用户名，仍溢出则隐藏浅色/深色切换按钮。
 */
function syncStartupHeaderOverflow(): void {
  const root = startupHeaderInnerRef.value
  if (!root) return
  hideStartupHeaderUserName.value = false
  hideStartupHeaderThemeBtn.value = false
  void nextTick(() => {
    if (root.scrollWidth <= root.clientWidth + 1) return
    hideStartupHeaderUserName.value = true
    void nextTick(() => {
      if (root.scrollWidth <= root.clientWidth + 1) return
      hideStartupHeaderThemeBtn.value = true
    })
  })
}

onMounted(() => {
  const root = startupHeaderInnerRef.value
  if (!root || typeof ResizeObserver === 'undefined') {
    syncStartupHeaderOverflow()
    return
  }
  startupHeaderResizeObserver = new ResizeObserver(() => {
    syncStartupHeaderOverflow()
  })
  startupHeaderResizeObserver.observe(root)
  syncStartupHeaderOverflow()
})

onBeforeUnmount(() => {
  startupHeaderResizeObserver?.disconnect()
  startupHeaderResizeObserver = null
})

// ── 新建作品表单与皮肤模板 ──

const RESOLUTION_OPTIONS: AppSelectOption[] = [
  { label: '1280 × 720', value: '1280x720' },
]

/**
 * 限制新建作品名称长度。
 * @param value 输入框原始内容
 */
function handleNewGameNameUpdate(value: string) {
  newGameName.value = truncateProjectName(value)
}

/** @param key 形如 `1280x720` 的分辨率标识 */
function parseResolutionKey(key: string): { width: number; height: number } {
  const [width, height] = key.split('x').map((value) => parseInt(value, 10))
  return {
    width: Number.isFinite(width) ? width : 1280,
    height: Number.isFinite(height) ? height : 720,
  }
}

function formatResolutionText(width: number, height: number) {
  return `${width} × ${height}`
}

const currentResolution = computed(() => parseResolutionKey(newResolution.value))

const availableTemplates = computed<BuiltinUiTemplate[]>(() => {
  return listBuiltinUiTemplates(currentResolution.value)
})

/** 当前分辨率下同步默认皮肤模板 */
function syncTemplateSelection() {
  const availableValues = new Set(availableTemplates.value.map((item) => item.id))
  if (newTemplateId.value && availableValues.has(newTemplateId.value)) return
  newTemplateId.value = availableTemplates.value[0]?.id || ''
}

const selectedTemplate = computed(() => getBuiltinUiTemplate(newTemplateId.value))

const templatePreviewCurrent = computed(() =>
  templatePreviewId.value ? getBuiltinUiTemplate(templatePreviewId.value) : null,
)

const templatePreviewGalleryUrls = computed(() => {
  const current = templatePreviewCurrent.value
  if (!current) return []
  return current.galleryUrls.length ? current.galleryUrls : [current.previewUrl]
})

// ── 云端作品列表 ──

watch(
  () => [auth.loggedIn, auth.uid] as const,
  ([loggedIn, uid]) => {
    if (!loggedIn || !uid) {
      cloudProjects.value = []
      cloudListLoading.value = false
      cloudListError.value = ''
      return
    }
    cloudListError.value = ''
    auth.ensureOssReady()
    void loadCloudProjects()
  },
  { immediate: true },
)

watch(
  () => newResolution.value,
  () => {
    syncTemplateSelection()
  },
  { immediate: true },
)

function formatTime(ts: number): string {
  try {
    return new Intl.DateTimeFormat('zh-CN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(ts))
  } catch {
    return String(ts)
  }
}

function toTimestamp(value: number | string | null | undefined): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value)
    if (Number.isFinite(n) && n > 0) return n
    const t = Date.parse(value)
    if (!Number.isNaN(t)) return t
  }
  return null
}

function cloudProjectSubtitle(item: CloudProjectListItem) {
  const parts: string[] = []
  if (item.resolution) parts.push(item.resolution)
  const ts = toTimestamp(item.updatedAt)
  if (ts !== null) parts.push(formatTime(ts))
  return parts.join(' · ') || `Key: ${item.projectKey}`
}

/** 加载「我的作品」列表 */
async function loadCloudProjects(): Promise<void> {
  if (!auth.loggedIn) return
  const requestToken = ++latestCloudListToken
  cloudListLoading.value = true
  cloudListError.value = ''
  try {
    const result = await loadCloudProjectList(auth.uid)
    if (requestToken !== latestCloudListToken || !auth.loggedIn) return
    cloudListConfigured.value = result.configured
    cloudProjects.value = result.items
  } catch (e: unknown) {
    if (requestToken !== latestCloudListToken || !auth.loggedIn) return
    cloudListConfigured.value = Boolean(auth.uid) && BrowserUtil.getInstance().connect(auth.uid).isConfigured()
    cloudProjects.value = []
    cloudListError.value = e instanceof Error ? e.message : '云端工程列表获取失败'
  } finally {
    if (requestToken === latestCloudListToken) {
      cloudListLoading.value = false
    }
  }
}

// ── 用户操作 ──

function openNewModal() {
  openError.value = ''
  if (!canUseProjectEntry.value) {
    message.warning('请先登录后再新建作品')
    return
  }
  newGameName.value = ''
  newResolution.value = '1280x720'
  syncTemplateSelection()
  newModalOpen.value = true
}

function openTemplatePicker() {
  syncTemplateSelection()
  templatePickerDraftId.value = newTemplateId.value
  templatePickerOpen.value = true
}

function closeTemplatePicker() {
  templatePickerOpen.value = false
  templatePreviewOpen.value = false
}

function confirmTemplatePicker() {
  newTemplateId.value = templatePickerDraftId.value
  templatePickerOpen.value = false
}

function openTemplatePreview(templateId: string) {
  templatePreviewId.value = templateId
  templatePreviewOpen.value = true
}

/** 从列表打开云端工程 */
async function openCloudProjectFromList(item: CloudProjectListItem) {
  openError.value = ''
  if (!auth.loggedIn) {
    message.warning('请先登录后再打开云端工程')
    return
  }
  try {
    const result = await workspace.openCloudProject(item.projectKey)
    await router.push({ name: 'workspace' })
    if (result.fontFallbackNotice) {
      message.warning(`当前工程设置字体缺失，已切换为默认字体【${result.fontFallbackNotice.fallbackFontName}】`)
    }
  } catch (e: unknown) {
    openError.value = e instanceof Error ? e.message : '打开云端工程失败'
  }
}

const startupRefreshLoading = computed(() =>
  startupTab.value === 'cloud' ? cloudListLoading.value : publishedListLoading.value,
)

/** 按当前 Tab 刷新列表 */
async function refreshStartupTab(): Promise<void> {
  if (startupTab.value === 'cloud') {
    await loadCloudProjects()
    return
  }
  await publishedTabRef.value?.reload()
}

/** 提交新建作品并进入工作区 */
async function submitNewProject() {
  const name = newGameName.value.trim()
  if (!name) {
    message.warning('请输入作品名称')
    return
  }
  if (hasInvalidProjectNameChar(name)) {
    message.warning(PROJECT_NAME_INVALID_CHAR_MESSAGE)
    return
  }
  if (!isProjectNameLengthValid(name)) {
    message.warning(PROJECT_NAME_LIMIT_MESSAGE)
    return
  }
  if (!canUseProjectEntry.value) {
    message.warning('请先登录后再新建作品')
    return
  }
  if (!selectedTemplate.value) {
    message.warning('当前分辨率暂无可用皮肤模板')
    return
  }
  newSubmitLoading.value = true
  try {
    const res = parseResolutionKey(newResolution.value)
    await workspace.createNewCloudProject(name, res, selectedTemplate.value.id)
    newModalOpen.value = false
    await router.push({ name: 'workspace' })
  } catch (e: unknown) {
    message.error(e instanceof Error ? e.message : '创建作品失败')
  } finally {
    newSubmitLoading.value = false
  }
}

watch(
  () => [auth.loggedIn, auth.displayName, auth.avatarUrl, themeStore.mode] as const,
  () => {
    syncStartupHeaderOverflow()
  },
)
</script>

<template>
  <!-- 顶栏 -->
  <n-layout class="startup-layout">
    <n-layout-header class="startup-header">
      <div
        ref="startupHeaderInnerRef"
        class="header-inner"
        :class="{
          'header-inner--hide-user-name': hideStartupHeaderUserName,
          'header-inner--hide-theme-btn': hideStartupHeaderThemeBtn,
        }"
      >
        <div class="startup-brand">
          <img class="startup-brand-logo" :src="logoUrl" alt="" />
          <span class="startup-brand-copy">
            <n-text strong class="startup-brand-text">橙易</n-text>
            <n-text depth="3" class="startup-version-text">{{ APP_DISPLAY_VERSION }}</n-text>
          </span>
        </div>
        <n-space align="center" :size="12" :wrap="false" class="startup-header-actions">
          <n-button
            size="small"
            quaternary
            circle
            class="startup-theme-btn"
            :title="themeStore.mode === 'dark' ? '切换浅色' : '切换深色'"
            :aria-label="themeStore.mode === 'dark' ? '切换浅色' : '切换深色'"
            @click="themeStore.toggle()"
          >
            <span class="startup-theme-icon" aria-hidden="true">
              <svg v-if="themeStore.mode === 'dark'" viewBox="0 0 24 24" focusable="false">
                <circle cx="12" cy="12" r="4.2" />
                <path d="M12 3.8V2.6" />
                <path d="M12 21.4v-1.2" />
                <path d="M3.8 12H2.6" />
                <path d="M21.4 12h-1.2" />
                <path d="m6.2 6.2-.9-.9" />
                <path d="m18.7 18.7-.9-.9" />
                <path d="m17.8 6.2.9-.9" />
                <path d="m5.3 18.7.9-.9" />
              </svg>
              <svg v-else viewBox="0 0 24 24" focusable="false">
                <path class="startup-theme-icon-fill" d="M17.6 15.2A6.7 6.7 0 0 1 8.8 6.4 7.2 7.2 0 1 0 17.6 15.2Z" />
              </svg>
            </span>
          </n-button>
          <template v-if="auth.loggedIn">
            <n-popconfirm @positive-click="auth.logout()">
              <template #trigger>
                <div class="user-trigger">
                  <AuthAvatar
                    :key="`${auth.uid}-${auth.avatarUrl}`"
                    :src="auth.avatarUrl || undefined"
                    :letter="auth.avatarLetter"
                    :alt="auth.displayName || '用户头像'"
                    :size="30"
                  />
                  <n-text depth="3" class="user-name">{{ auth.displayName }}</n-text>
                </div>
              </template>
              确认退出登录？
            </n-popconfirm>
          </template>
          <n-button v-else size="small" @click="auth.startLogin()">登录</n-button>
        </n-space>
      </div>
    </n-layout-header>

    <!-- 主区：新建 + 作品 Tab -->
    <n-layout-content class="startup-content">
      <div class="content-wrap">
        <n-space vertical :size="24" class="startup-stack">
          <n-space :size="10" wrap class="hero-actions">
            <n-button
              type="primary"
              size="medium"
              class="startup-new-btn"
              :disabled="!canUseProjectEntry"
              @click="openNewModal"
            >
              新建作品
            </n-button>
          </n-space>

          <n-alert v-if="openError" type="error" :show-icon="false" class="startup-open-error">
            {{ openError }}
          </n-alert>

          <n-card :bordered="false" class="recent-card" size="small">
            <n-tabs v-model:value="startupTab" type="line" animated>
              <template #suffix>
                <n-button
                  v-if="auth.loggedIn"
                  quaternary
                  circle
                  size="tiny"
                  class="startup-refresh-btn"
                  :loading="startupRefreshLoading"
                  title="刷新"
                  aria-label="刷新"
                  @click="refreshStartupTab"
                >
                  <span class="startup-refresh-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" focusable="false">
                      <path d="M19 8a7 7 0 0 0-12.1-3.2L5 6.8" />
                      <path d="M5 3.5v3.3h3.3" />
                      <path d="M5 16a7 7 0 0 0 12.1 3.2L19 17.2" />
                      <path d="M19 20.5v-3.3h-3.3" />
                    </svg>
                  </span>
                </n-button>
              </template>
              <n-tab-pane name="cloud" tab="我的作品" display-directive="show">
                <template v-if="!auth.loggedIn">
                  <n-empty description="登录后可查看并打开我的作品" />
                </template>
                <template v-else-if="cloudListLoading">
                  <div class="panel-state">正在加载我的作品…</div>
                </template>
                <template v-else-if="cloudListError">
                  <div class="panel-state panel-state-error">{{ cloudListError }}</div>
                </template>
                <template v-else-if="!cloudListConfigured">
                  <n-empty description="未配置我的作品列表能力" />
                </template>
                <template v-else-if="cloudProjects.length">
                  <n-list clickable :show-divider="false" class="startup-cloud-list">
                    <n-list-item
                      v-for="item in cloudProjects"
                      :key="item.projectKey"
                      class="startup-cloud-item"
                      @click="openCloudProjectFromList(item)"
                    >
                      <n-thing :title="item.projectName" :description="cloudProjectSubtitle(item)" />
                    </n-list-item>
                  </n-list>
                </template>
                <n-empty v-else description="暂无作品" />
              </n-tab-pane>
              <n-tab-pane name="published" tab="已发布作品" display-directive="show">
                <PublishedProjectTab
                  ref="publishedTabRef"
                  :active="startupTab === 'published'"
                  @loading-change="publishedListLoading = $event"
                />
              </n-tab-pane>
            </n-tabs>
          </n-card>
        </n-space>
      </div>
    </n-layout-content>

    <!-- 新建作品 -->
    <n-modal
      v-model:show="newModalOpen"
      preset="card"
      title="新建作品"
      class="startup-modal-np"
      :mask-closable="false"
    >
      <n-form label-placement="left" label-width="96">
        <n-form-item label="作品名称">
          <div class="project-name-field">
            <n-input
              :value="newGameName"
              placeholder="请输入作品名称"
              @update:value="handleNewGameNameUpdate"
            />
            <div class="project-name-hint">不超过15个汉字或30个英文</div>
          </div>
        </n-form-item>
        <n-form-item label="分辨率">
          <AppSelectField v-model="newResolution" wide :options="RESOLUTION_OPTIONS" />
        </n-form-item>
        <n-form-item label="皮肤模板">
          <div class="startup-template-field">
            <div v-if="selectedTemplate" class="startup-template-summary">
              <img
                :src="selectedTemplate.previewUrl"
                :alt="`${selectedTemplate.label} 预览图`"
                class="startup-template-summary__cover"
              />
              <div class="startup-template-summary__meta">
                <div class="startup-template-summary__title">{{ selectedTemplate.label }}</div>
                <div class="startup-template-summary__sub">
                  {{ formatResolutionText(selectedTemplate.width, selectedTemplate.height) }}
                  · {{ Math.max(1, selectedTemplate.galleryUrls.length) }} 张预览
                </div>
              </div>
            </div>
            <n-button size="small" @click="openTemplatePicker">
              选择皮肤
            </n-button>
          </div>
        </n-form-item>
      </n-form>
      <n-space justify="end" class="startup-modal-actions">
        <n-button @click="newModalOpen = false">取消</n-button>
        <n-button type="primary" :loading="newSubmitLoading" @click="submitNewProject">创建</n-button>
      </n-space>
    </n-modal>

    <!-- 皮肤模板选择 -->
    <n-modal
      v-model:show="templatePickerOpen"
      preset="card"
      title="选择皮肤模板"
      class="startup-modal-template-picker"
      :mask-closable="false"
    >
      <div class="startup-template-picker">
        <div class="startup-template-picker__heading">
          当前分辨率：{{ formatResolutionText(currentResolution.width, currentResolution.height) }}
        </div>
        <div v-if="availableTemplates.length" class="startup-template-picker__list">
          <div
            v-for="template in availableTemplates"
            :key="template.id"
            class="startup-template-card"
            :class="{ 'startup-template-card--active': templatePickerDraftId === template.id }"
          >
            <button
              type="button"
              class="startup-template-card__main"
              @click="templatePickerDraftId = template.id"
            >
              <img
                :src="template.previewUrl"
                :alt="`${template.label} 封面`"
                class="startup-template-card__cover"
              />
              <div class="startup-template-card__title">{{ template.label }}</div>
              <div class="startup-template-card__sub">{{ formatResolutionText(template.width, template.height) }}</div>
            </button>
            <div class="startup-template-card__actions">
              <button
                type="button"
                class="startup-template-card__action"
                @click="openTemplatePreview(template.id)"
              >
                预览
              </button>
            </div>
          </div>
        </div>
        <n-empty
          v-else
          description="当前分辨率暂无皮肤模板，暂时无法新建作品。"
          class="startup-template-picker__empty"
        />
      </div>
      <n-space justify="end" class="startup-modal-actions">
        <n-button @click="closeTemplatePicker">取消</n-button>
        <n-button type="primary" :disabled="!templatePickerDraftId" @click="confirmTemplatePicker">
          确认
        </n-button>
      </n-space>
    </n-modal>

    <!-- 皮肤预览 -->
    <n-modal
      v-model:show="templatePreviewOpen"
      preset="card"
      title="皮肤预览"
      class="startup-modal-template-preview"
      :mask-closable="false"
    >
      <div v-if="templatePreviewCurrent" class="startup-template-preview">
        <div class="startup-template-preview__heading">
          <div class="startup-template-preview__title">{{ templatePreviewCurrent.label }}</div>
          <div class="startup-template-preview__sub">
            {{ formatResolutionText(templatePreviewCurrent.width, templatePreviewCurrent.height) }}
            · {{ templatePreviewGalleryUrls.length }} 张预览
          </div>
        </div>
        <div class="startup-template-gallery__grid">
          <img
            v-for="(url, index) in templatePreviewGalleryUrls"
            :key="`${templatePreviewCurrent.id}-${index}`"
            :src="url"
            :alt="`${templatePreviewCurrent.label} 预览 ${index + 1}`"
            class="startup-template-gallery__image"
          />
        </div>
      </div>
      <n-space justify="end" class="startup-modal-actions">
        <n-button @click="templatePreviewOpen = false">关闭</n-button>
      </n-space>
    </n-modal>
  </n-layout>
</template>

<!-- 样式：布局 / 顶栏 / 列表 / 弹窗 / 竖屏 -->
<style scoped>
.startup-layout {
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  background: var(--theme-bg-surface);
  color: var(--theme-text-primary);
  touch-action: manipulation;
}

.startup-header {
  padding: 0;
  background: var(--theme-bg-surface);
  border-bottom: 0;
}

.header-inner {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 8px 16px;
  box-sizing: border-box;
  overflow: hidden;
}

.header-inner--hide-user-name .user-name {
  display: none;
}

.header-inner--hide-theme-btn .startup-theme-btn {
  display: none;
}

.startup-brand-text {
  display: inline-block;
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
  color: var(--theme-text-heading);
}

.startup-brand {
  display: inline-flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.startup-brand-logo {
  display: block;
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.startup-brand-copy {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 4px 6px;
}

.startup-header-actions {
  flex: 0 0 auto;
  min-width: 0;
}

.startup-header-actions :deep(.n-space) {
  flex-wrap: nowrap !important;
}

.startup-theme-btn {
  --n-width: 34px;
  --n-height-small: 34px;
  --n-padding: 0;
  --n-border: 1px solid transparent;
  --n-border-hover: 1px solid transparent;
  --n-border-pressed: 1px solid transparent;
  --n-color: transparent;
  --n-color-hover: color-mix(in srgb, var(--theme-accent) 10%, var(--theme-bg-panel));
  --n-color-pressed: color-mix(in srgb, var(--theme-accent) 16%, var(--theme-bg-panel));
  --n-text-color: var(--theme-text-secondary);
  --n-text-color-hover: var(--theme-accent);
  --n-text-color-pressed: var(--theme-accent);
  --n-border-radius: 999px;
}

.startup-theme-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  line-height: 0;
}

.startup-theme-icon svg {
  display: block;
  width: 23px;
  height: 23px;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
}

.startup-theme-icon-fill {
  fill: currentColor;
  stroke: none;
}

.startup-version-text {
  display: inline-block;
  font-size: 11px;
  line-height: 1;
  color: var(--theme-text-secondary);
}

.startup-content {
  background: var(--theme-bg-surface);
}

.content-wrap {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 18px 16px 28px;
  box-sizing: border-box;
}

.startup-stack {
  width: 100%;
}

.startup-open-error {
  white-space: pre-line;
}

.hero-actions {
  padding: 4px 0 0;
}

.startup-new-btn {
  min-width: 96px;
}

.recent-card {
  height: 100%;
  min-width: 0;
  background: var(--theme-bg-panel) !important;
  border: 0 !important;
}

.recent-card :deep(.n-card__content),
.recent-card :deep(.n-tabs),
.recent-card :deep(.n-tab-pane) {
  min-width: 0;
  overflow-x: hidden;
}

.panel-state {
  padding: 10px 0;
  font-size: 11px;
  color: var(--theme-text-very-muted);
}

.panel-state-error {
  color: var(--theme-error);
}

.startup-cloud-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 0;
  background: var(--theme-bg-surface);
}

.startup-cloud-item {
  padding: 12px 16px !important;
  background: var(--theme-bg-card) !important;
}

.startup-cloud-item:hover {
  background: var(--theme-bg-hover) !important;
}

.startup-refresh-btn {
  color: var(--theme-text-secondary);
}

.startup-refresh-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.startup-refresh-icon svg {
  display: block;
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.startup-modal-actions {
  width: 100%;
  margin-top: 8px;
}

.startup-template-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.startup-template-summary {
  display: grid;
  grid-template-columns: 144px minmax(0, 1fr);
  gap: 10px;
  width: 100%;
  min-width: 0;
}

.startup-template-summary__cover {
  display: block;
  width: 144px;
  height: 82px;
  object-fit: contain;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
}

.startup-template-summary__meta {
  min-width: 0;
}

.startup-template-summary__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--theme-text-heading);
}

.startup-template-summary__sub,
.startup-template-picker__heading {
  font-size: 12px;
  color: var(--theme-text-secondary);
}

.startup-template-picker {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: min(72vh, 680px);
  overflow: hidden;
}

.startup-template-picker__list {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  min-height: 0;
  overflow: auto;
  padding-right: 2px;
}

.startup-template-picker__empty {
  padding: 12px 0 6px;
}

.startup-template-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
}

.startup-template-card--active {
  border-color: var(--theme-accent);
}

.startup-template-card__main {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
  padding: 8px 8px 6px;
  border: 0;
  background: transparent;
  color: var(--theme-text-heading);
  cursor: pointer;
  text-align: left;
}

.startup-template-card__cover {
  display: block;
  width: 100%;
  height: 108px;
  object-fit: contain;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
}

.startup-template-card__title {
  font-size: 13px;
  font-weight: 600;
}

.startup-template-card__actions {
  padding: 0 8px 8px;
}

.startup-template-card__action {
  width: 100%;
  height: 28px;
  padding: 0 8px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
  color: var(--theme-text-secondary);
  cursor: pointer;
}

.startup-template-card__sub,
.startup-template-preview__sub {
  font-size: 12px;
  color: var(--theme-text-secondary);
}

.startup-template-preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.startup-template-preview__heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.startup-template-preview__title {
  font-size: 14px;
  font-weight: 600;
  color: var(--theme-text-heading);
}

.startup-template-gallery__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  max-height: min(68vh, 760px);
  overflow: auto;
}

.startup-template-gallery__image {
  display: block;
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
}

.user-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 6px;
  border: 0;
  background: transparent;
}

.user-name {
  max-width: min(36vw, 160px);
  overflow: hidden;
  line-height: 30px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-name-field {
  width: 100%;
  min-width: 0;
}

.project-name-hint {
  margin-top: 4px;
  font-size: 12px;
  color: var(--theme-text-muted);
  line-height: 18px;
  white-space: nowrap;
}

:global(body .startup-modal-np.n-modal) {
  width: min(520px, 92vw);
  max-width: 90vw;
  box-sizing: border-box;
}

:global(body .startup-modal-template-picker.n-modal) {
  width: min(920px, 94vw);
  max-width: 94vw;
  box-sizing: border-box;
}

:global(body .startup-modal-template-preview.n-modal) {
  width: min(980px, 94vw);
  max-width: 94vw;
  box-sizing: border-box;
}

:global(.recent-card .n-card__content) {
  padding-top: 8px !important;
}

@media (max-width: 1180px) and (orientation: portrait) {
  .recent-card :deep(.n-tabs-nav) {
    flex-wrap: wrap;
    row-gap: 4px;
  }

  .recent-card :deep(.n-tabs-tab),
  .recent-card :deep(.n-tabs-tab__label) {
    writing-mode: horizontal-tb;
    text-orientation: mixed;
    white-space: nowrap;
  }

  .startup-cloud-item :deep(.n-thing-header__title),
  .startup-cloud-item :deep(.n-thing-main__description) {
    writing-mode: horizontal-tb;
    text-orientation: mixed;
    white-space: normal;
    word-break: break-word;
  }
}

@media (max-width: 860px) {
  .startup-template-summary,
  .startup-template-gallery__grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .startup-template-picker__list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .startup-template-summary__cover {
    width: 100%;
    height: 160px;
  }
}
</style>
